import client from 'api/client'
import cx from 'classnames'
import Banner from 'common/Banner/Banner'
import BannerContent from 'common/BannerContent/BannerContent'
import { MetaTags } from 'components/MetaTags'
import { useLayers } from 'hooks/queries/layers/useLayers'
import { useDebouncedResizeObserver } from 'hooks/useDebouncedResizeObserver'
import { useFadingScrollbar } from 'hooks/useFadingScrollbar'
import { useInformalFeedbackRequests } from 'hooks/useInformalFeedbackRequests'
import { useOnScroll } from 'hooks/UseOnScroll'
import useIsMounted from 'ismounted'
import { useEffect, useMemo, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import { ArticleFlat, Profile, UserRole } from 'types'
import { getCreatorId, getTextContentFromHtmlString } from 'utils'
import { isExternalResource } from 'utils/articleUtils'
import { MAIN_CONTENT_ID } from './Constants'
import Styles from './ProseEdit.module.scss'
import { useInformalFeedbackRequestRealtimeChannel } from '../../hooks/useInformalFeedbackRequestRealtimeChannel'
import { ArticleReader } from './components/ArticleReader/ArticleReader'
import { ExternalResourceReader } from './components/ExternalResourceReader/ExternalResourceReader'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useWorkspace } from 'hooks/useWorkspace'
import { SubjectType } from 'utils/caslAbility'

interface Props {
  article: ArticleFlat
  layerId: string
}

const ProseRead = ({ article, layerId }: Props) => {
  const [stickyActionBar, setStickyActionBar] = useState(false)
  const [prevScrollY, setPrevScrollY] = useState<number>()
  const actionBarRef = useRef<HTMLDivElement>(null)
  const [creator, setCreator] = useState<Profile>()
  const { ref: rootContainerRef, width: rootWidth } = useDebouncedResizeObserver<HTMLDivElement>(50)
  const creatorId = useMemo(() => getCreatorId(article), [article])
  const isMounted = useIsMounted()
  const { data: layersData } = useLayers({ articleKey: article.id })
  const layer = layersData?.layers.find((layer) => layer.id === layerId)
  const isArticleExternalResource = isExternalResource(article)
  const { isMobile } = useResponsiveDesign()
  const workspace = useWorkspace(article.company_id)
  const { isChannelSocialPostable } = useIsChannelPostable()

  const mainContainer = useRef<HTMLElement>(null)
  useFadingScrollbar(mainContainer.current)

  const [leftRailOpen, setLeftRailOpen] = useState(false)
  useEffect(() => {
    if (rootWidth == null) return
    setLeftRailOpen(window.innerWidth - rootWidth > 0)
  }, [rootWidth])

  useEffect(() => {
    if (creatorId) {
      client.getUserCard(creatorId).then((e) => {
        if (e.type === 'success' && isMounted.current) setCreator(e.data.user)
      })
    }
  }, [creatorId, isMounted])

  useOnScroll(() => {
    let padding = -65
    // add heights of any components that are rendered above `ActionBar`, like `NavBar`
    padding += 20

    const actionBarHeight = actionBarRef.current ? actionBarRef.current.clientHeight : 0

    const deltaY = window.scrollY - (prevScrollY || 0)
    if (window.scrollY > actionBarHeight + padding && !stickyActionBar && deltaY > 0) {
      setStickyActionBar(true)
    }
    if (window.scrollY <= actionBarHeight + padding && stickyActionBar && deltaY < 0) {
      setStickyActionBar(false)
    }
    setPrevScrollY(window.scrollY)
  })

  let metaDescription = article.why_trust_this || article.meta_description || ''
  if (!metaDescription && creator) {
    metaDescription = `by ${creator.first_name} ${creator.last_name}`
    if (creator.company_name) metaDescription += `, ${creator.company_name}`
  }

  useInformalFeedbackRequests(article.id, layerId)
  useInformalFeedbackRequestRealtimeChannel(article.id, layerId)

  const authedArticle = {
    role: workspace?.role || UserRole.UNAUTHED,
    __caslSubjectType__: SubjectType.ARTICLE,
  }

  if (!layer) {
    return null
  }

  return (
    <>
      <div
        ref={rootContainerRef}
        className={cx(Styles.container, {
          [Styles.leftRailOpen]: leftRailOpen,
          [Styles.leftRailClosed]: !leftRailOpen,
          [Styles.hidden]:
            isMobile &&
            isChannelSocialPostable(article.channel) &&
            ![UserRole.UNAUTHED, UserRole.GUEST].includes(authedArticle.role),
        })}
      >
        <Helmet defer={false}>
          {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-nested-ternary
            article.is_stub || !article.made_public ? (
              // No index if stub or not made public
              <meta name="robots" content="noindex,nofollow" data-react-helmet="true" />
            ) : article.noindex || article.nofollow ? (
              // Allow manual noindex/nofollow override for public articles
              <meta
                name="robots"
                content={cx({ nofollow: article.nofollow, noindex: article.noindex }).replace(' ', ', ')}
                data-react-helmet="true"
              />
            ) : null
          }
        </Helmet>
        <MetaTags
          url={article.article_link}
          title={article.title}
          description={metaDescription}
          // temporarily use researchContent for description meta tag (for search seo) until we get a better description
          // @see https://www.pivotaltracker.com/n/projects/2414179/stories/172299374
          searchDescription={article.why_trust_this || getTextContentFromHtmlString(article.our_research_process)}
          twitterLargeImage
        />
        <section className={Styles.content}>
          <main ref={mainContainer} id={MAIN_CONTENT_ID} className={Styles.main}>
            {article.is_stub && (
              <Banner className={Styles.banner}>
                <BannerContent
                  heading="For contributors"
                  text="To answer this doc request, make a copy (the green button in the top right corner of this page) to fill in the doc. Once you're done, publish and share your doc with the Blaze community."
                />
              </Banner>
            )}
            <div
              className={cx(Styles.belowActionBarContainer, { [Styles.externalResource]: isArticleExternalResource })}
            >
              <div className={Styles.articleContentsContainer}>
                {isArticleExternalResource && <ExternalResourceReader article={article} />}
                {!isArticleExternalResource && (
                  <ArticleReader article={article} layer={layer} creator={creator} leftRailOpen={leftRailOpen} />
                )}
              </div>
            </div>
          </main>
        </section>
      </div>
    </>
  )
}

export default ProseRead
